import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { db, storage } from "../firebase";
import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { getDownloadURL, listAll, ref } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { loadGoogleMaps } from "../mapLoader";
import {
  Search, MapPin, X, Navigation, ShieldCheck,
  Star, Clock, SlidersHorizontal
} from "lucide-react";

/* ── avatar fallback ── */
function makeAvatar(name) {
  const initials = (name || "D").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  const colors = ["#3b82f6", "#6366f1", "#8b5cf6", "#10b981"];
  const bg = colors[(name || "").length % colors.length];
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
      <rect width="200" height="200" fill="${bg}"/>
      <text x="50%" y="50%" text-anchor="middle" dy=".35em" fill="#fff" font-size="80" font-family="Arial" font-weight="bold">${initials}</text>
    </svg>`
  )}`;
}

/* ── haversine distance ── */
function distKm(a, b) {
  const R = 6371;
  const toRad = d => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

/* ── custom map styles — clean indigo-tinted ── */
const MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#f8f7ff" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#4338ca" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#e0e7ff" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#c7d2fe" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#a5b4fc" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#bfdbfe" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#ede9fe" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];

/* ── custom SVG marker ── */
function makeMarkerSvg(selected) {
  const bg = selected ? "#4338ca" : "#6366f1";
  const size = selected ? 44 : 36;
  return {
    url: `data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size + 8}" viewBox="0 0 ${size} ${size + 8}">
        <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${bg}" stroke="white" stroke-width="3"/>
        <polygon points="${size / 2 - 6},${size - 2} ${size / 2 + 6},${size - 2} ${size / 2},${size + 8}" fill="${bg}"/>
        <text x="${size / 2}" y="${size / 2 + 5}" text-anchor="middle" fill="white" font-size="12" font-weight="bold" font-family="Arial">DR</text>
      </svg>`
    )}`,
    scaledSize: { width: size, height: size + 8 },
    anchor: { x: size / 2, y: size + 8 },
  };
}

export default function MapDoctors() {
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const navigate = useNavigate();

  const [isMapReady, setIsMapReady] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [center, setCenter] = useState({ lat: 41.3275, lng: 19.8187 });
  const [radiusKm, setRadiusKm] = useState(25);
  const [search, setSearch] = useState("");
  const [selectedSpec, setSelectedSpec] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [locErr, setLocErr] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [showPanel, setShowPanel] = useState(true);

  const mapRef = useRef(null);
  const gmap = useRef(null);
  const markersRef = useRef([]);
  const circleRef = useRef(null);
  const infowinRef = useRef(null);

  /* responsive */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* load google maps */
  useEffect(() => {
    let alive = true;
    if (!GOOGLE_MAPS_API_KEY) return;
    loadGoogleMaps(GOOGLE_MAPS_API_KEY).then(ok => { if (alive) setIsMapReady(!!ok); });
    return () => { alive = false; };
  }, [GOOGLE_MAPS_API_KEY]);

  /* load doctors */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const snap = await getDocs(collection(db, "doctors"));
        const rows = await Promise.all(
          snap.docs.map(async d => {
            const data = d.data();
            const coords = data?.location?.coordinates;
            if (!coords?.lat || !coords?.lng) return null;

            let photoURL = data.photoURL || data.photoUrl || data.profilePicture || "";
            if (!photoURL) {
              try {
                const folderRef = ref(storage, `doctor_photos/${d.id}`);
                const files = await listAll(folderRef);
                if (files.items.length > 0) photoURL = await getDownloadURL(files.items[0]);
              } catch {}
            }

            return {
              id: d.id,
              name: data.name || "Doctor",
              specialty: data.specialty || data.specialtySq || "General",
              specialtyKey: data.specialtyKey || "",
              city: data.city || data?.location?.address || "",
              rating: parseFloat((data.rating ?? 4.5).toFixed(1)),
              feeText: data.feeText || data.priceText || "",
              experience: (data.experienceYears ?? data.experience ?? "5+").toString(),
              verified: data.isVerified === true || data.verified === true,
              mode: data.mode || "",
              position: { lat: coords.lat, lng: coords.lng },
              photoURL: photoURL || makeAvatar(data.name || "D"),
            };
          })
        );
        if (alive) setDoctors(rows.filter(Boolean));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  /* specialties list */
  const specialties = useMemo(() => {
    const keys = new Map();
    doctors.forEach(d => {
      if (d.specialtyKey) keys.set(d.specialtyKey, d.specialty);
    });
    return Array.from(keys.entries()).map(([key, label]) => ({ key, label }));
  }, [doctors]);

  /* filtered + sorted by distance */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return doctors
      .filter(d => {
        const dist = distKm(center, d.position);
        if (dist > radiusKm) return false;
        if (selectedSpec && d.specialtyKey !== selectedSpec) return false;
        if (q && !(d.name.toLowerCase().includes(q) || d.specialty.toLowerCase().includes(q))) return false;
        return true;
      })
      .map(d => ({ ...d, dist: distKm(center, d.position) }))
      .sort((a, b) => a.dist - b.dist);
  }, [doctors, center, radiusKm, search, selectedSpec]);

  /* init map */
  useEffect(() => {
    if (!isMapReady || !mapRef.current || gmap.current) return;
    gmap.current = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 12,
      styles: MAP_STYLES,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: false,
    });
    circleRef.current = new window.google.maps.Circle({
      strokeColor: "#6366f1",
      strokeOpacity: 0.4,
      strokeWeight: 1.5,
      fillColor: "#6366f1",
      fillOpacity: 0.06,
      map: gmap.current,
      center,
      radius: radiusKm * 1000,
    });
    infowinRef.current = new window.google.maps.InfoWindow();
  }, [isMapReady]);

  /* update markers + circle */
  useEffect(() => {
    if (!gmap.current) return;
    if (circleRef.current) {
      circleRef.current.setCenter(center);
      circleRef.current.setRadius(radiusKm * 1000);
    }
    gmap.current.setCenter(center);
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    filtered.forEach(d => {
      const isSelected = selectedDoctor?.id === d.id;
      const iconSvg = makeMarkerSvg(isSelected);
      const marker = new window.google.maps.Marker({
        position: d.position,
        map: gmap.current,
        title: d.name,
        icon: {
          url: iconSvg.url,
          scaledSize: new window.google.maps.Size(iconSvg.scaledSize.width, iconSvg.scaledSize.height),
          anchor: new window.google.maps.Point(iconSvg.anchor.x, iconSvg.anchor.y),
        },
        zIndex: isSelected ? 10 : 1,
      });
      marker.addListener("click", () => {
        setSelectedDoctor(d);
        gmap.current.panTo(d.position);
        if (isMobile) setShowPanel(false);
      });
      markersRef.current.push(marker);
    });
  }, [filtered, center, radiusKm, selectedDoctor, isMobile]);

  /* geolocation */
  const useMyLocation = useCallback(() => {
    setLocErr("");
    if (!navigator.geolocation) { setLocErr("Geolocation not supported."); return; }
    navigator.geolocation.getCurrentPosition(
      pos => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(c);
        if (gmap.current) { gmap.current.setCenter(c); gmap.current.setZoom(13); }
      },
      () => setLocErr("Could not get your location."),
      { timeout: 15000, enableHighAccuracy: true }
    );
  }, []);

  const searchThisArea = useCallback(() => {
    if (!gmap.current) return;
    const c = gmap.current.getCenter();
    setCenter({ lat: c.lat(), lng: c.lng() });
  }, []);

  const zoomIn = () => gmap.current && gmap.current.setZoom(gmap.current.getZoom() + 1);
  const zoomOut = () => gmap.current && gmap.current.setZoom(gmap.current.getZoom() - 1);

  /* ── RENDER ── */
  return (
    <div style={{ height: "100vh", display: "grid", gridTemplateRows: "56px 1fr", background: "#f8f7ff", overflow: "hidden" }}>

      {/* ── HEADER ── */}
      <header style={{
        background: "#fff", borderBottom: "1px solid #e0e7ff",
        display: "flex", alignItems: "center", gap: 10, padding: "0 16px",
        zIndex: 20,
      }}>
        <div style={{ fontSize: 15, fontWeight: 900, color: "#1e1b4b", marginRight: "auto", display: "flex", alignItems: "center", gap: 7 }}>
          <MapPin size={16} color="#6366f1" />
          Harta e Doktorëve
        </div>

        {filtered.length > 0 && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 20, padding: "4px 12px", fontSize: 11, fontWeight: 800, color: "#4338ca" }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#6366f1" }} />
            {filtered.length} doktorë aty pranë
          </div>
        )}

        <select
          value={radiusKm}
          onChange={e => setRadiusKm(parseInt(e.target.value))}
          style={{ background: "#f8f7ff", border: "1.5px solid #e0e7ff", borderRadius: 10, padding: "6px 10px", fontSize: 12, fontWeight: 700, color: "#1e1b4b", cursor: "pointer" }}
        >
          {[5, 10, 15, 25, 50, 100].map(r => (
            <option key={r} value={r}>{r} km</option>
          ))}
        </select>

        <button
          onClick={useMyLocation}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "#6366f1", color: "#fff", border: "none", borderRadius: 10, padding: "7px 14px", fontSize: 12, fontWeight: 900, cursor: "pointer" }}
        >
          <Navigation size={13} />
          {!isMobile && "Vendndodhja ime"}
        </button>

        {isMobile && (
          <button
            onClick={() => setShowPanel(p => !p)}
            style={{ background: "#f5f3ff", color: "#4338ca", border: "1.5px solid #c7d2fe", borderRadius: 10, padding: "7px 12px", fontSize: 12, fontWeight: 800, cursor: "pointer" }}
          >
            <SlidersHorizontal size={14} />
          </button>
        )}
      </header>

      {/* ── BODY ── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : (showPanel ? "340px 1fr" : "0 1fr"), overflow: "hidden", transition: "grid-template-columns .3s" }}>

        {/* ── LEFT PANEL ── */}
        {(!isMobile || showPanel) && (
          <div style={{ background: "#fff", borderRight: "1px solid #e0e7ff", display: "flex", flexDirection: "column", overflow: "hidden", zIndex: 10 }}>

            {/* Search + filters */}
            <div style={{ padding: "12px 14px", borderBottom: "1px solid #e0e7ff", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8f7ff", border: "1.5px solid #e0e7ff", borderRadius: 10, padding: "8px 12px", marginBottom: 10 }}>
                <Search size={14} color="#6366f1" style={{ flexShrink: 0 }} />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Kërko specialitet, emër…"
                  style={{ border: "none", background: "transparent", outline: "none", fontSize: 13, fontWeight: 600, color: "#1e1b4b", width: "100%" }}
                />
                {search && (
                  <button onClick={() => setSearch("")} style={{ border: "none", background: "none", cursor: "pointer", padding: 0 }}>
                    <X size={13} color="#a5b4fc" />
                  </button>
                )}
              </div>

              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                <button
                  onClick={() => setSelectedSpec("")}
                  style={{ fontSize: 10, fontWeight: 800, background: !selectedSpec ? "#6366f1" : "#f5f3ff", color: !selectedSpec ? "#fff" : "#4338ca", border: `1.5px solid ${!selectedSpec ? "#6366f1" : "#c7d2fe"}`, borderRadius: 20, padding: "4px 10px", cursor: "pointer" }}
                >
                  Të gjithë
                </button>
                {specialties.slice(0, 6).map(s => (
                  <button
                    key={s.key}
                    onClick={() => setSelectedSpec(selectedSpec === s.key ? "" : s.key)}
                    style={{ fontSize: 10, fontWeight: 800, background: selectedSpec === s.key ? "#6366f1" : "#f5f3ff", color: selectedSpec === s.key ? "#fff" : "#4338ca", border: `1.5px solid ${selectedSpec === s.key ? "#6366f1" : "#c7d2fe"}`, borderRadius: 20, padding: "4px 10px", cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Count */}
            <div style={{ padding: "8px 14px", borderBottom: "1px solid #e0e7ff", fontSize: 11, fontWeight: 800, color: "#6366f1", flexShrink: 0 }}>
              {loading ? "Duke ngarkuar…" : `${filtered.length} doktorë brenda ${radiusKm} km`}
            </div>

            {locErr && (
              <div style={{ padding: "8px 14px", fontSize: 12, fontWeight: 700, color: "#dc2626", background: "#fef2f2", borderBottom: "1px solid #fecaca", flexShrink: 0 }}>
                {locErr}
              </div>
            )}

            {/* Doctor list */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {filtered.length === 0 && !loading && (
                <div style={{ padding: "24px 14px", textAlign: "center", color: "#94a3b8", fontSize: 13, fontWeight: 700 }}>
                  Nuk u gjetën doktorë.<br />Zgjero rrezën ose ndrysho filtrat.
                </div>
              )}
              {filtered.map(d => {
                const isSelected = selectedDoctor?.id === d.id;
                return (
                  <div
                    key={d.id}
                    onClick={() => {
                      setSelectedDoctor(d);
                      gmap.current?.panTo(d.position);
                      gmap.current?.setZoom(14);
                      if (isMobile) setShowPanel(false);
                    }}
                    style={{
                      padding: "12px 14px",
                      borderBottom: "1px solid #f0f0f8",
                      cursor: "pointer",
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      background: isSelected ? "#eef2ff" : "#fff",
                      borderLeft: isSelected ? "3px solid #6366f1" : "3px solid transparent",
                      transition: "background .15s",
                    }}
                  >
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      <img
                        src={d.photoURL}
                        alt={d.name}
                        style={{ width: 46, height: 46, borderRadius: 12, objectFit: "cover", border: "2px solid #e0e7ff", display: "block" }}
                        onError={e => { e.target.src = makeAvatar(d.name); }}
                      />
                      {d.verified && (
                        <div style={{ position: "absolute", bottom: -2, right: -2, background: "#6366f1", borderRadius: "50%", width: 14, height: 14, border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <ShieldCheck size={7} color="#fff" />
                        </div>
                      )}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 900, color: "#1e1b4b", marginBottom: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {d.name}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#6366f1", marginBottom: 5 }}>
                        {d.specialty}
                      </div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 4 }}>
                        <span style={{ fontSize: 9, fontWeight: 800, background: "#f5f3ff", color: "#4338ca", borderRadius: 5, padding: "2px 6px", display: "inline-flex", alignItems: "center", gap: 2 }}>
                          <Star size={8} color="#f59e0b" fill="#f59e0b" /> {d.rating}
                        </span>
                        {d.feeText && (
                          <span style={{ fontSize: 9, fontWeight: 900, color: "#059669", background: "#dcfce7", borderRadius: 5, padding: "2px 6px" }}>
                            {d.feeText}
                          </span>
                        )}
                        {d.mode && (
                          <span style={{ fontSize: 9, fontWeight: 800, background: "#dcfce7", color: "#166534", borderRadius: 5, padding: "2px 6px" }}>
                            {d.mode}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 9, fontWeight: 800, color: "#64748b", display: "flex", alignItems: "center", gap: 4 }}>
                        <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#10b981", flexShrink: 0 }} />
                        {d.dist.toFixed(1)} km larg
                        {d.city ? ` · ${d.city}` : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── MAP ── */}
        <div style={{ position: "relative", overflow: "hidden" }}>
          <div ref={mapRef} style={{ width: "100%", height: "100%" }} />

          {/* Zoom controls */}
          <div style={{ position: "absolute", right: 12, top: 12, display: "flex", flexDirection: "column", gap: 6, zIndex: 5 }}>
            <button onClick={zoomIn} style={{ width: 36, height: 36, background: "#fff", border: "1px solid #e0e7ff", borderRadius: 10, fontSize: 18, fontWeight: 900, color: "#4338ca", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.06)" }}>+</button>
            <button onClick={zoomOut} style={{ width: 36, height: 36, background: "#fff", border: "1px solid #e0e7ff", borderRadius: 10, fontSize: 18, fontWeight: 900, color: "#4338ca", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.06)" }}>−</button>
          </div>

          {/* Search this area */}
          <div style={{ position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 5 }}>
            <button
              onClick={searchThisArea}
              style={{ background: "#fff", border: "1px solid #e0e7ff", borderRadius: 20, padding: "7px 16px", fontSize: 12, fontWeight: 800, color: "#4338ca", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,.08)", display: "flex", alignItems: "center", gap: 6 }}
            >
              <Search size={12} /> Kërko këtu
            </button>
          </div>

          {/* Radius badge */}
          <div style={{ position: "absolute", bottom: selectedDoctor ? 180 : 16, left: 12, zIndex: 5, background: "rgba(255,255,255,.95)", border: "1px solid #e0e7ff", borderRadius: 20, padding: "5px 12px", fontSize: 11, fontWeight: 800, color: "#4338ca", boxShadow: "0 2px 8px rgba(0,0,0,.06)", transition: "bottom .3s" }}>
            Rrezja: {radiusKm} km · {filtered.length} rezultate
          </div>

          {/* Selected doctor popup */}
          {selectedDoctor && (
            <div style={{
              position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
              background: "#fff", border: "1px solid #e0e7ff", borderRadius: 18,
              padding: 14, width: 300, maxWidth: "calc(100vw - 32px)",
              boxShadow: "0 8px 32px rgba(99,102,241,.15)",
              zIndex: 10,
              display: "flex", gap: 12, alignItems: "flex-start",
            }}>
              <div style={{ position: "relative", flexShrink: 0 }}>
                <img
                  src={selectedDoctor.photoURL}
                  alt={selectedDoctor.name}
                  style={{ width: 52, height: 52, borderRadius: 14, objectFit: "cover", border: "2px solid #e0e7ff", display: "block" }}
                  onError={e => { e.target.src = makeAvatar(selectedDoctor.name); }}
                />
                {selectedDoctor.verified && (
                  <div style={{ position: "absolute", bottom: -2, right: -2, background: "#6366f1", borderRadius: "50%", width: 16, height: 16, border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ShieldCheck size={8} color="#fff" />
                  </div>
                )}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: "#1e1b4b", marginBottom: 2 }}>
                  {selectedDoctor.name}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#6366f1", marginBottom: 6 }}>
                  {selectedDoctor.specialty}
                  {selectedDoctor.city ? ` · ${selectedDoctor.city}` : ""}
                  {selectedDoctor.dist != null ? ` · ${selectedDoctor.dist.toFixed(1)} km` : ""}
                </div>
                <div style={{ display: "flex", gap: 5, marginBottom: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, fontWeight: 800, background: "#f5f3ff", color: "#4338ca", borderRadius: 6, padding: "2px 7px", display: "inline-flex", alignItems: "center", gap: 3 }}>
                    <Star size={9} color="#f59e0b" fill="#f59e0b" /> {selectedDoctor.rating}
                  </span>
                  {selectedDoctor.feeText && (
                    <span style={{ fontSize: 10, fontWeight: 900, color: "#059669", background: "#dcfce7", borderRadius: 6, padding: "2px 7px" }}>
                      {selectedDoctor.feeText}
                    </span>
                  )}
                  {selectedDoctor.experience && (
                    <span style={{ fontSize: 10, fontWeight: 800, background: "#f5f3ff", color: "#4338ca", borderRadius: 6, padding: "2px 7px" }}>
                      {selectedDoctor.experience} vjet
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 7 }}>
                  <button
                    onClick={() => navigate(`/doctor/${selectedDoctor.id}`)}
                    style={{ flex: 1, background: "#6366f1", color: "#fff", border: "none", borderRadius: 10, padding: "8px", fontSize: 12, fontWeight: 900, cursor: "pointer" }}
                  >
                    Rezervo →
                  </button>
                  <button
                    onClick={() => navigate(`/doctor/${selectedDoctor.id}`)}
                    style={{ background: "#f5f3ff", color: "#4338ca", border: "1.5px solid #c7d2fe", borderRadius: 10, padding: "8px 12px", fontSize: 12, fontWeight: 800, cursor: "pointer" }}
                  >
                    Profili
                  </button>
                </div>
              </div>

              <button
                onClick={() => setSelectedDoctor(null)}
                style={{ position: "absolute", top: 10, right: 10, background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0 }}
              >
                <X size={16} />
              </button>
            </div>
          )}

          {/* No API key message */}
          {!GOOGLE_MAPS_API_KEY && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#f8f7ff" }}>
              <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 14, fontWeight: 700 }}>
                <MapPin size={32} color="#c7d2fe" style={{ marginBottom: 8 }} />
                <div>REACT_APP_GOOGLE_MAPS_API_KEY missing</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}